
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Sparkles, BookOpen, Wand2, UserCircle } from "lucide-react";
import { motion } from "framer-motion";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { MobileNav } from "./MobileNav";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useEffect, useState } from "react";

export const Header = ({ user }: { user: any }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    if (user) {
      const fetchProfile = async () => {
        const { data } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();
        
        if (data) {
          setProfile(data);
        }
      };

      fetchProfile();
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign out",
        variant: "destructive",
      });
    }
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    if (!user) return "";
    const email = user.email || "";
    return email.charAt(0).toUpperCase();
  };

  return (
    <header className="border-b bg-gradient-to-r from-[#FFE29F]/10 to-[#FF719A]/10">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <motion.button 
            onClick={() => navigate("/")} 
            className="flex items-center gap-2 text-xl font-bold text-primary hover:opacity-90 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1, 1.1, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop"
              }}
            >
              <Sparkles className="h-6 w-6 text-primary" />
            </motion.div>
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              MiniMakes
            </span>
          </motion.button>

          <div className="flex items-center gap-4">
            <MobileNav user={user} />
            
            <div className="hidden lg:flex items-center gap-4">
              {user && (
                <>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="ghost"
                      onClick={() => navigate("/")}
                      className="gap-2"
                    >
                      <Wand2 className="h-4 w-4" />
                      Transform Recipe
                    </Button>
                  </motion.div>

                  <NavigationMenu>
                    <NavigationMenuList>
                      <NavigationMenuItem>
                        <NavigationMenuTrigger>
                          <span className="flex items-center gap-2">
                            <BookOpen className="h-4 w-4" />
                            Your Journal
                          </span>
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <div className="p-4 w-[240px] space-y-3">
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => navigate("/journal?tab=entries")}
                            >
                              Your Cooking Journey
                            </Button>
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => navigate("/journal?tab=new")}
                            >
                              Add New Entry
                            </Button>
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                </>
              )}

              <div className="hidden lg:flex gap-4">
                {user ? (
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => navigate("/profile")}
                      className="flex items-center gap-2 p-2"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={profile?.avatar_url} alt="Profile" />
                        <AvatarFallback className="bg-primary/10">
                          {getInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden xl:inline">Profile</span>
                    </Button>
                    
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button onClick={handleLogout} variant="outline" className="border-primary/20 hover:bg-primary/5">
                        Sign Out
                      </Button>
                    </motion.div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button 
                        onClick={() => navigate("/auth")} 
                        variant="outline"
                        className="border-primary/20 hover:bg-primary/5"
                      >
                        Sign In
                      </Button>
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button 
                        onClick={() => navigate("/auth?mode=signup")} 
                        className="bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        Sign Up
                      </Button>
                    </motion.div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
